import { Encoder, MGPFallible } from '@everyboard/lib';
import { Coord } from '../../jscaip/Coord';
import { Move } from '../../jscaip/Move';
import { MoveCoord } from '../../jscaip/MoveCoord';
import { MoveCoordToCoord } from '../../jscaip/MoveCoordToCoord';
import { MoveWithTwoCoords } from '../../jscaip/MoveWithTwoCoords';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { HivePiece } from './HivePiece';
export class HiveDropMove extends MoveCoord {
    piece;
    static encoder = Encoder.tuple([HivePiece.encoder, Coord.encoder], (move) => [move.piece, move.coord], (fields) => new HiveDropMove(fields[0], fields[1].x, fields[1].y));
    static of(piece, coord) {
        return new HiveDropMove(piece, coord.x, coord.y);
    }
    constructor(piece, x, y) {
        super(x, y);
        this.piece = piece;
    }
    toString() {
        return `HiveDrop(${this.piece.toString()}, ${this.coord.toString()})`;
    }
    equals(other) {
        if (other instanceof HiveDropMove) {
            return this.piece.equals(other.piece) && this.coord.equals(other.coord);
        }
        return false;
    }
}
export class HiveCoordToCoordMove extends MoveCoordToCoord {
    static encoder = MoveWithTwoCoords.getFallibleEncoder(HiveCoordToCoordMove.from);
    static from(start, end) {
        if (start.equals(end)) {
            return MGPFallible.failure(RulesFailure.MOVE_CANNOT_BE_STATIC());
        }
        return MGPFallible.success(new HiveCoordToCoordMove(start, end));
    }
    constructor(start, end) {
        super(start, end);
    }
    toString() {
        return `HiveMoveCoordToCoord(${this.getStart().toString()} -> ${this.getEnd().toString()})`;
    }
    equals(other) {
        if (other instanceof HiveSpiderMove) {
            // Spider moves are coord to coord but the intermediate coords matter, so we can't compare them here
            return false;
        }
        if (other instanceof HiveCoordToCoordMove) {
            return this.getStart().equals(other.getStart()) && this.getEnd().equals(other.getEnd());
        }
        return false;
    }
}
export class HiveSpiderMove extends HiveCoordToCoordMove {
    coords;
    static encoder = Encoder.tuple([Coord.encoder, Coord.encoder, Coord.encoder, Coord.encoder], (move) => move.coords, (fields) => new HiveSpiderMove(fields));
    static ofCoords(coords) {
        return new HiveSpiderMove(coords);
    }
    constructor(coords) {
        super(coords[0], coords[3]);
        this.coords = coords;
    }
    toString() {
        const coords = this.coords.map((coord) => coord.toString()).join(', ');
        return `HiveMoveSpider(${coords})`;
    }
    equals(other) {
        if (other instanceof HiveSpiderMove) {
            return this.getStart().equals(other.getStart()) && this.getEnd().equals(other.getEnd());
        }
        return false;
    }
}
export class HivePassMove extends Move {
    static encoder = Encoder.constant('HiveMovePass', new HivePassMove());
    toString() {
        return 'HiveMovePass';
    }
    equals(other) {
        return other instanceof HivePassMove;
    }
}
function isInstanceOfHiveDropMove(value) {
    return value instanceof HiveDropMove;
}
function isInstanceOfHiveMoveSpider(value) {
    return value instanceof HiveSpiderMove;
}
function isInstanceOfHiveMoveCoordToCoord(value) {
    return value instanceof HiveCoordToCoordMove;
}
function isInstanceOfHiveMovePass(value) {
    return value instanceof HivePassMove;
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
export var HiveMove;
(function (HiveMove) {
    HiveMove.PASS = new HivePassMove();
    function drop(piece, coord) {
        return HiveDropMove.of(piece, coord);
    }
    HiveMove.drop = drop;
    function move(start, end) {
        return HiveCoordToCoordMove.from(start, end);
    }
    HiveMove.move = move;
    function spiderMove(coords) {
        return HiveSpiderMove.ofCoords(coords);
    }
    HiveMove.spiderMove = spiderMove;
    HiveMove.encoder = Encoder.disjunction([
        isInstanceOfHiveDropMove,
        isInstanceOfHiveMoveSpider,
        isInstanceOfHiveMoveCoordToCoord,
        isInstanceOfHiveMovePass,
    ], [HiveDropMove.encoder, HiveSpiderMove.encoder, HiveCoordToCoordMove.encoder, HivePassMove.encoder]);
})(HiveMove || (HiveMove = {}));
